
const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');
const libTabBar = require('../views/TabBar.js');

/**
 * AccessWorkspace — the orchestrator behind the cohesive "Access & Permissions" workspace.
 *
 * One screen replaces the raw Role / RolePermission / RoleEdge / UserRoleJoin recordset lists: a rail of
 * subjects (Roles, Permission Groups, Users) + a workspace-global context bar + a tabbed editor. This
 * provider owns the ONLY copy of the selected subject + context ({ SubjectKind, SubjectID, Context,
 * IDContext }); the sibling providers (PermissionMatrix, PermissionVisualizer, RoleAssignment) read it and
 * never duplicate it. All render state lives in pict.AppData.AccessWorkspace and the AccessWorkspace view
 * derives + repaints declaratively (the MasterListManager / ConfigViewer idiom).
 *
 * A Permission Group is a Role row with Type='PermissionGroup'; a Role Group is a Role with any other Type.
 * This rich layer is separate from the simple User.IDRole (0-5) base level, which stays in user management.
 */

// Tabs per subject kind. 'details' is the only one wired in phase 0; the rest are filled by later phases
// (Permissions=matrix, Effective=visualizer, Composition/Members=groups, assignment/overrides=user).
const TABS_ROLE =
[
	{ Key: 'details', Label: 'Details', Icon: 'fas fa-id-card' },
	{ Key: 'permissions', Label: 'Permissions', Icon: 'fas fa-table-cells' },
	{ Key: 'effective', Label: 'Effective', Icon: 'fas fa-eye' },
	{ Key: 'composition', Label: 'Composition', Icon: 'fas fa-sitemap' },
	{ Key: 'members', Label: 'Members', Icon: 'fas fa-users' },
];

// Shown only for a User Group (Role.Type==='UserGroup'): the effective set of projects/labs a user is added to
// when placed in the group (UserGroupGrants provider). Appended to TABS_ROLE in _deriveSubject for that kind.
const TAB_PROJECTS_GRANTED = { Key: 'projectsgranted', Label: 'Projects Granted', Icon: 'fas fa-folder-tree' };

const TABS_USER =
[
	{ Key: 'details', Label: 'Details', Icon: 'fas fa-id-card' },
	{ Key: 'assignment', Label: 'Roles & Groups', Icon: 'fas fa-user-tag' },
	{ Key: 'effective', Label: 'Effective', Icon: 'fas fa-eye' },
	{ Key: 'overrides', Label: 'Overrides', Icon: 'fas fa-user-shield' },
];

// Context scopes a permission can be authored/resolved in. Customer is the common case; the others take a
// context entity ID (a contract / project / lab record).
const CONTEXT_OPTIONS =
[
	{ Key: 'Customer', Label: 'Customer' },
	{ Key: 'Contract', Label: 'Contract' },
	{ Key: 'Project', Label: 'Project' },
	{ Key: 'Lab', Label: 'Lab' },
];

// Tabs that actually render content today; everything else shows a "coming with phase N" placeholder so the
// shell is shippable + verifiable on its own. Later phases move keys out of this set as they land.
const READY_TABS = { details: true, permissions: true, effective: true, composition: true, members: true, assignment: true, overrides: true, projectsgranted: true };

class AccessWorkspaceProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// Raw subject lists (loaded once per session, refreshed on demand). Roles/Groups are fully loaded (they feed
		// name lookups + the composition/assignment pickers); Users are server-side search results (a customer can have
		// thousands, so they are never preloaded — see _searchUsersAsync).
		this._roles = [];
		this._permissionGroups = [];
		this._users = [];
		this._railLoaded = false;
		this._userSearchTimer = null;
		this._userSearchToken = 0;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	// --- Session context + role gating (mirrors MasterListRegistry + PermissionService) ---

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	get idUser()
	{
		return this.currentUser.UserID;
	}

	/**
	 * Customer-1 / role-5 super-user (the server's EndpointBase._isSuperUser).
	 * @return {Boolean}
	 */
	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		if ((Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1))
		{
			return true;
		}
		// The home app (PermissionService.isSuperUser) also treats the SuperAdminAccessRole as a super-user.
		const tmpRoles = tmpUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && pRole.Hash === 'SuperAdminAccessRole');
	}

	/**
	 * Holds a role with PermissionLevel 'RoleManager' (or is a super-user) — the home app's gate for
	 * editing roles/permissions (PermissionService.isRoleManager). UI hint only; the server is authoritative.
	 * @return {Boolean}
	 */
	get isRoleManager()
	{
		if (this.isSuperUser)
		{
			return true;
		}
		// Mirror PermissionService.isRoleManager (RoleManager level), plus the SuperAdminUser permission level.
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'RoleManager' || pRole.PermissionLevel === 'SuperAdminUser'));
	}

	/**
	 * All roles + permission groups currently loaded in the rail (for name lookups + the composition picker).
	 * @return {Array}
	 */
	get rolesAndGroups()
	{
		return this._roles.concat(this._permissionGroups);
	}

	/**
	 * Is the current subject a User Group (Role.Type==='UserGroup')? Only User Groups grant context memberships
	 * (RoleContextMembership) that member_sync materializes into user projects/labs — so only they get the
	 * "Projects Granted" tab.
	 * @return {Boolean}
	 */
	get isUserGroup()
	{
		return ((this.state.SubjectRecord || {}).Type === 'UserGroup');
	}

	// --- REST helpers (promise wrappers over the Headlight rest client) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			// The rest client exposes deleteEntity(entity, id) (there is no raw deleteJSON). Entity/<id> paths
			// go through it; custom DELETE paths (e.g. Permissions/Role/.../For/User/...) go direct.
			const tmpMatch = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (tmpMatch)
			{
				this.restClient.deleteEntity(tmpMatch[1], tmpMatch[2], (pError, pBody) => pError ? reject(pError) : resolve(pBody));
				return;
			}
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((pResponse) =>
				{
					if (!pResponse.ok) { reject(new Error(`HTTP ${pResponse.status}`)); return; }
					pResponse.text().then((pText) => resolve(pText ? JSON.parse(pText) : {})).catch(() => resolve({}));
				})
				.catch(reject);
		});
	}

	/**
	 * Read an entire filtered set (Count then a single page sized to the count). Meadow's FilteredTo needs an
	 * explicit page size, so we size it from the Count rather than guessing.
	 * @param {String} pEntitySet - plural meadow set (e.g. 'Roles', 'Users')
	 * @param {String} pFilter - a FoxHound FilteredTo expression
	 * @param {Number} [pCap] - hard cap on rows fetched (default 1000)
	 * @return {Promise<Array>}
	 */
	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 1000;
		let tmpTotal = tmpCap;
		try
		{
			const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`);
			tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap);
		}
		catch (pError)
		{
			tmpTotal = tmpCap;
		}
		if (!tmpTotal)
		{
			return [];
		}
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	// --- Workspace state (drives the AccessWorkspace view via AppData) ---

	get state()
	{
		if (!this.pict.AppData.AccessWorkspace)
		{
			this.pict.AppData.AccessWorkspace =
			{
				SubjectKind: '', SubjectID: 0, SubjectLabel: '', SubjectRecord: null,
				Context: 'Customer', IDContext: 0, ContextIDInput: '',
				ActiveTab: 'details',
				Search: '', RailLoading: false,
				Message: '', MessageClass: '', Saving: false, DeleteConfirm: false,
				// Details-tab "add members": a multi-select user picker writes ids here. For an existing role they're
				// assigned on the Add button; for a NEW role they're staged and assigned right after Save creates it.
				DetailsAddUserIDs: [], DetailsAddSaving: false, DetailsAddError: '', DetailsAddNotice: '',
				// derived slots
				RailGroups: [], RailLoadingSlot: [], MessageSlot: [], NewButtonsSlot: [],
				SubjectSlot: [], EmptySlot: [ {} ],
				Tabs: [], ContextOptions: [], ContextIDSlot: [],
				SubjectTitle: '', SubjectIcon: '', SubjectKindLabel: '',
				DetailsActiveSlot: [], DetailsFields: [],
				PlaceholderActiveSlot: [], PlaceholderText: '',
			};
		}
		return this.pict.AppData.AccessWorkspace;
	}

	// --- Boot / rail load ---

	/**
	 * Load (once) the rail subjects and paint. Called by the /Access route.
	 * @param {Boolean} [pForce] - reload even if already loaded
	 */
	async loadAsync(pForce)
	{
		if (this._railLoaded && !pForce)
		{
			this._render();
			return;
		}
		const tmpState = this.state;
		tmpState.RailLoading = true;
		if (!tmpState.IDContext)
		{
			tmpState.IDContext = this.idCustomer;
		}
		this._render();
		try
		{
			await this._loadRailAsync();
			this._railLoaded = true;
		}
		catch (pError)
		{
			this._message(`Could not load roles and users: ${pError && pError.message ? pError.message : pError}`, 'is-danger');
		}
		tmpState.RailLoading = false;
		this._render();
	}

	async _loadRailAsync()
	{
		const tmpCustomer = this.idCustomer;
		// Roles (+ Permission Groups) — global (IDCustomer 0) + this customer, not deleted. Load the FULL set
		// (_getFilteredAll = Count-then-page): the rolesAndGroups getter feeds name lookups AND the composition /
		// assignment pickers, so this set must never be truncated. Roles are admin-authored (bounded; ~1.5k on a large
		// customer). The high cap is only a pathological backstop, not the old silent 1000-row cutoff.
		const tmpRoleFilter = `FBL~IDCustomer~INN~0,${tmpCustomer}~FBV~Deleted~EQ~0`;
		const tmpRoles = await this._getFilteredAll('Roles', tmpRoleFilter, 10000);
		this._roles = tmpRoles.filter((pRole) => (pRole.Type !== 'PermissionGroup'));
		this._permissionGroups = tmpRoles.filter((pRole) => (pRole.Type === 'PermissionGroup'));
		// Users are NOT preloaded — a customer can have thousands (9,495 seen), so the old `Users/…/0/100` cap silently
		// hid everyone past the first 100 and the client-side search could never reach them. The rail's Users group is
		// server-side search now (setSearch → _searchUsersAsync); it starts empty until the admin types.
		this._users = [];
	}

	// --- Subject selection ---

	async selectRoleAsync(pID)
	{
		return this._selectRoleRecordAsync(pID, 'role');
	}

	async selectGroupAsync(pID)
	{
		return this._selectRoleRecordAsync(pID, 'group');
	}

	async _selectRoleRecordAsync(pID, pKind)
	{
		const tmpState = this.state;
		const tmpID = parseInt(pID, 10);
		tmpState.SubjectKind = pKind;
		tmpState.SubjectID = tmpID;
		// Land a role/group on its Permissions matrix — the primary thing managed here.
		tmpState.ActiveTab = 'permissions';
		tmpState.Message = '';
		this._render();
		try
		{
			const tmpRole = await this._getJSON(`Role/${tmpID}`);
			tmpState.SubjectRecord = tmpRole || null;
			tmpState.SubjectLabel = (tmpRole && tmpRole.Name) || `Role ${tmpID}`;
		}
		catch (pError)
		{
			tmpState.SubjectRecord = null;
			this._message(`Could not load that ${pKind}.`, 'is-danger');
		}
		this._render();
		this._loadActiveTabData();
	}

	async selectUserAsync(pID)
	{
		const tmpState = this.state;
		const tmpID = parseInt(pID, 10);
		tmpState.SubjectKind = 'user';
		tmpState.SubjectID = tmpID;
		tmpState.ActiveTab = 'details';
		tmpState.Message = '';
		this._render();
		try
		{
			const tmpUser = await this._getJSON(`User/${tmpID}`);
			tmpState.SubjectRecord = tmpUser || null;
			tmpState.SubjectLabel = tmpUser ? libUserSearch.userLabel(tmpUser) : `User ${tmpID}`;
		}
		catch (pError)
		{
			tmpState.SubjectRecord = null;
			this._message('Could not load that user.', 'is-danger');
		}
		this._render();
	}

	/**
	 * Open a role/group by its GUID — the PSRS read-route bridge from the raw Role list.
	 * @param {String} pGUID
	 */
	async openRoleByGUIDAsync(pGUID)
	{
		this.loadAsync();
		try
		{
			const tmpRows = await this._getFilteredAll('Roles', `FBV~GUIDRole~EQ~${pGUID}`, 1);
			if (tmpRows.length)
			{
				const tmpRole = tmpRows[0];
				this.selectSubject((tmpRole.Type === 'PermissionGroup') ? 'group' : 'role', tmpRole.IDRole);
			}
		}
		catch (pError)
		{
			this._message('Could not open that role.', 'is-danger');
			this._render();
		}
	}

	/**
	 * Dispatch a rail click to the right subject loader.
	 * @param {String} pKind - 'role' | 'group' | 'user'
	 * @param {Number} pID
	 */
	selectSubject(pKind, pID)
	{
		// A different subject → drop any users staged in the Details add-members picker (don't carry them across roles).
		this._resetDetailsAdd();
		if (pKind === 'user')
		{
			return this.selectUserAsync(pID);
		}
		if (pKind === 'group')
		{
			return this.selectGroupAsync(pID);
		}
		return this.selectRoleAsync(pID);
	}

	/** Clear the Details add-members picker state (on subject change / new). */
	_resetDetailsAdd()
	{
		const tmpState = this.state;
		tmpState.DetailsAddUserIDs = [];
		tmpState.DetailsAddSaving = false;
		tmpState.DetailsAddError = '';
		tmpState.DetailsAddNotice = '';
	}

	// --- Role / Group CRUD ---

	newRole()
	{
		this._startNew('role', 'Role');
	}

	newGroup()
	{
		this._startNew('group', 'PermissionGroup');
	}

	_startNew(pKind, pType)
	{
		const tmpState = this.state;
		tmpState.SubjectKind = pKind;
		tmpState.SubjectID = 0;
		tmpState.SubjectRecord = { IDRole: 0, Name: '', Type: pType, PermissionLevel: '', Description: '', IDCustomer: this.idCustomer };
		tmpState.SubjectLabel = (pKind === 'group') ? 'New permission group' : 'New role';
		tmpState.ActiveTab = 'details';
		tmpState.DeleteConfirm = false;
		tmpState.Message = '';
		this._resetDetailsAdd();
		this._render();
	}

	/**
	 * Update a field of the in-edit role/group record. Text fields don't repaint (so the input keeps focus);
	 * the Type select repaints to refresh the subject icon/label.
	 * @param {String} pField
	 * @param {String} pValue
	 */
	setDetailField(pField, pValue)
	{
		if (!this.state.SubjectRecord)
		{
			return;
		}
		this.state.SubjectRecord[pField] = pValue;
		if (pField === 'Type')
		{
			this.state.SubjectKind = (pValue === 'PermissionGroup') ? 'group' : 'role';
			this._render();
		}
	}

	/**
	 * Create (IDRole 0) or update the selected role/group, then refresh the rail + keep it selected.
	 */
	async saveDetailsAsync()
	{
		const tmpState = this.state;
		if (tmpState.Saving)
		{
			return;
		}
		const tmpRecord = tmpState.SubjectRecord || {};
		if (!tmpRecord.Name || !String(tmpRecord.Name).trim())
		{
			this._message('A name is required.', 'is-warning');
			this._render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Message = '';
		this._render();
		const tmpType = tmpRecord.Type || 'Role';
		const tmpPayload =
		{
			Name: String(tmpRecord.Name).trim(),
			Type: tmpType,
			PermissionLevel: tmpRecord.PermissionLevel || '',
			Description: tmpRecord.Description || '',
			IDCustomer: tmpRecord.IDCustomer || this.idCustomer,
		};
		const tmpWasNew = !(Number(tmpState.SubjectID) > 0);
		try
		{
			let tmpSaved;
			if (!tmpWasNew)
			{
				tmpPayload.IDRole = Number(tmpState.SubjectID);
				tmpSaved = await this._putJSON('Role', tmpPayload);
			}
			else
			{
				tmpSaved = await this._postJSON('Role', tmpPayload);
			}
			const tmpNewID = (tmpSaved && tmpSaved.IDRole) || Number(tmpState.SubjectID);
			await this._loadRailAsync();
			this._railLoaded = true;
			tmpState.SubjectID = tmpNewID;
			tmpState.SubjectKind = (tmpType === 'PermissionGroup') ? 'group' : 'role';
			tmpState.SubjectRecord = tmpSaved || Object.assign({ IDRole: tmpNewID }, tmpPayload);
			tmpState.SubjectLabel = tmpPayload.Name;
			tmpState.Saving = false;
			// Create Role screen: assign any members staged on the form now that the role has an id.
			if (tmpWasNew && (tmpNewID > 0) && (tmpState.DetailsAddUserIDs || []).length)
			{
				const tmpStaged = tmpState.DetailsAddUserIDs.slice();
				tmpState.DetailsAddUserIDs = [];
				this._message('Saved. Adding members…', 'is-success is-light');
				this._render();
				const tmpAssign = await this._assignUsersToRoleAsync(tmpNewID, tmpStaged);
				tmpState.DetailsAddNotice = tmpAssign.fail
					? `Added ${tmpAssign.ok} member${tmpAssign.ok === 1 ? '' : 's'}; ${tmpAssign.fail} could not be added (you may lack permission).`
					: `Added ${tmpAssign.ok} member${tmpAssign.ok === 1 ? '' : 's'} to this role.`;
			}
			this._message('Saved.', 'is-success is-light');
			this._render();
		}
		catch (pError)
		{
			tmpState.Saving = false;
			this._message(`Could not save: ${pError && pError.message ? pError.message : pError}`, 'is-danger');
			this._render();
		}
	}

	requestDelete()
	{
		this.state.DeleteConfirm = true;
		this._render();
	}

	cancelDelete()
	{
		this.state.DeleteConfirm = false;
		this._render();
	}

	/**
	 * Delete the selected role/group, then refresh the rail + clear the selection.
	 */
	async confirmDeleteAsync()
	{
		const tmpState = this.state;
		if (Number(tmpState.SubjectID) <= 0 || tmpState.Saving)
		{
			return;
		}
		tmpState.Saving = true;
		this._render();
		try
		{
			await this._deleteJSON(`Role/${tmpState.SubjectID}`);
			await this._loadRailAsync();
			this._railLoaded = true;
			tmpState.SubjectKind = '';
			tmpState.SubjectID = 0;
			tmpState.SubjectRecord = null;
			tmpState.DeleteConfirm = false;
			tmpState.Saving = false;
			this._message('Deleted.', 'is-success is-light');
			this._render();
		}
		catch (pError)
		{
			tmpState.Saving = false;
			this._message(`Could not delete: ${pError && pError.message ? pError.message : pError}`, 'is-danger');
			this._render();
		}
	}

	// --- Tab + context controls ---

	/** Switch tabs via a stable route (#/Access/<Kind>/<ID>/<tab>) so the subject AND its active tab both live in
	 *  the URL — deep-linkable, bookmarkable, back/forward. The tab buttons call this; the subject-route handler
	 *  applies the tab (and only re-selects/re-fetches the subject when it actually changed). */
	navigateTab(pTab)
	{
		const tmpState = this.state;
		const tmpKindPath = { role: 'Role', group: 'Group', user: 'User' }[tmpState.SubjectKind];
		if (tmpKindPath && tmpState.SubjectID && (typeof window !== 'undefined') && window.location)
		{
			window.location.hash = `#/Access/${ tmpKindPath }/${ tmpState.SubjectID }/${ pTab }`;
		}
		else
		{
			this.setActiveTab(pTab);
		}
	}

	setActiveTab(pKey)
	{
		this.state.ActiveTab = pKey;
		this._render();
		this._loadActiveTabData();
	}

	setContext(pContext)
	{
		const tmpState = this.state;
		tmpState.Context = pContext;
		if (pContext === 'Customer')
		{
			tmpState.IDContext = this.idCustomer;
			tmpState.ContextIDInput = '';
		}
		else
		{
			// Other scopes take an explicit context entity ID typed below.
			tmpState.IDContext = parseInt(tmpState.ContextIDInput, 10) || 0;
		}
		this._onContextChanged();
	}

	setContextID(pValue)
	{
		const tmpState = this.state;
		tmpState.ContextIDInput = pValue;
		tmpState.IDContext = parseInt(pValue, 10) || 0;
		this._onContextChanged();
	}

	/**
	 * Hook for the data tabs to refresh when the context changes. Phase 0 just repaints; later phases
	 * override-extend this to re-read the matrix / visualizer for the new Context+IDContext.
	 */
	_onContextChanged()
	{
		this._render();
		this._loadActiveTabData();
	}

	setSearch(pValue)
	{
		this.state.Search = pValue || '';
		// Roles/Groups are fully loaded → filter them client-side immediately (snappy). Users are server-side search:
		// debounce 250ms then query (mirrors SuperUserGrant / Entitlements), with a stale-token guard on the response.
		this._render();
		if (this._userSearchTimer) { clearTimeout(this._userSearchTimer); }
		this._userSearchTimer = setTimeout(() => { this._userSearchTimer = null; this._searchUsersAsync(); }, 250);
	}

	/**
	 * Server-side user search for the rail's Users group (the shared user search: name, login ID or email, every word
	 * must match), scoped to this customer + not deleted, in name order, capped at 50. Below 2 characters the group
	 * clears — browsing thousands of users is exactly the failure mode we're removing. Guarded by a token so a slow
	 * response from an earlier keystroke can't overwrite a newer one.
	 */
	async _searchUsersAsync()
	{
		const tmpQuery = String(this.state.Search || '').trim();
		if (tmpQuery.length < 2)
		{
			this._users = [];
			this._render();
			return;
		}
		const tmpToken = ++this._userSearchToken;
		let tmpUsers = [];
		try
		{
			tmpUsers = await libUserSearch.searchUsersAsync((pURL) => this._getJSON(pURL), tmpQuery, { Scope: `FBV~IDCustomer~EQ~${this.idCustomer}~FBV~Deleted~EQ~0`, PageSize: 50 });
		}
		catch (pError)
		{
			tmpUsers = [];
		}
		if (tmpToken !== this._userSearchToken)
		{
			return;
		}
		this._users = tmpUsers;
		this._render();
	}

	/**
	 * Trigger the owning provider's data load for the active tab (on subject select, tab switch, or context
	 * change). Each data tab is backed by its own provider but reads subject + context from here.
	 */
	_loadActiveTabData()
	{
		const tmpState = this.state;
		if (!tmpState.SubjectRecord)
		{
			return;
		}
		if (tmpState.ActiveTab === 'permissions' && tmpState.SubjectKind !== 'user' && this.pict.providers.PermissionMatrix)
		{
			this.pict.providers.PermissionMatrix.loadAsync();
		}
		if (tmpState.ActiveTab === 'effective' && this.pict.providers.PermissionVisualizer)
		{
			this.pict.providers.PermissionVisualizer.loadAsync();
		}
		if (tmpState.ActiveTab === 'composition' && tmpState.SubjectKind !== 'user' && this.pict.providers.GroupComposition)
		{
			this.pict.providers.GroupComposition.loadAsync();
		}
		if (tmpState.ActiveTab === 'members' && tmpState.SubjectKind !== 'user' && this.pict.providers.GroupComposition)
		{
			this.pict.providers.GroupComposition.loadMembersAsync();
		}
		if (tmpState.ActiveTab === 'projectsgranted' && this.isUserGroup && this.pict.providers.UserGroupGrants)
		{
			this.pict.providers.UserGroupGrants.loadGrantsAsync();
		}
		if ((tmpState.ActiveTab === 'assignment' || tmpState.ActiveTab === 'overrides') && tmpState.SubjectKind === 'user' && this.pict.providers.RoleAssignment)
		{
			this.pict.providers.RoleAssignment.loadAsync();
		}
	}

	// --- Helpers ---


	_message(pMessage, pClass)
	{
		const tmpState = this.state;
		tmpState.Message = pMessage;
		tmpState.MessageClass = pClass || 'is-info';
	}

	_matchesSearch(pLabel)
	{
		const tmpSearch = (this.state.Search || '').toLowerCase().trim();
		if (!tmpSearch)
		{
			return true;
		}
		return String(pLabel || '').toLowerCase().indexOf(tmpSearch) > -1;
	}

	// --- Render ---

	_render()
	{
		this.deriveRenderState();
		if (this.pict.views.AccessWorkspace)
		{
			this.pict.views.AccessWorkspace.render();
		}
	}

	/**
	 * Recompute the AppData the AccessWorkspace view renders.
	 */
	deriveRenderState()
	{
		const tmpState = this.state;

		tmpState.RailLoadingSlot = tmpState.RailLoading ? [ {} ] : [];
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message, MessageClass: tmpState.MessageClass || 'is-info' } ] : [];
		tmpState.NewButtonsSlot = this.isRoleManager ? [ {} ] : [];

		// Rail groups. Roles/Groups are fully loaded → client-filtered by the search box. Users are server-side search
		// results (already filtered) → shown as-is, with a hint prompting a search when the group is empty.
		const tmpQuery = String(tmpState.Search || '').trim();
		const tmpUsersGroup = this._railGroup('users', 'Users', 'fas fa-users', this._users, 'user', true);
		tmpUsersGroup.HintSlot = tmpUsersGroup.Rows.length
			? []
			: [ { Hint: (tmpQuery.length < 2) ? 'Type at least 2 characters to find a user.' : `No users match “${tmpQuery}”.` } ];
		tmpState.RailGroups =
		[
			this._railGroup('roles', 'Roles', 'fas fa-user-shield', this._roles, 'role', false),
			this._railGroup('groups', 'Permission Groups', 'fas fa-layer-group', this._permissionGroups, 'group', false),
			tmpUsersGroup,
			// Roles/Groups collapse when empty under a search; the Users group always shows (it carries the search hint).
		].filter((pGroup) => (pGroup.Key === 'users') || pGroup.Rows.length > 0 || !tmpState.Search);

		// Context bar.
		tmpState.ContextOptions = CONTEXT_OPTIONS.map((pOption) =>
			({ Key: pOption.Key, Label: pOption.Label, SelectedAttr: (pOption.Key === tmpState.Context) ? 'selected' : '' }));
		tmpState.ContextIDSlot = (tmpState.Context === 'Customer') ? [] : [ { ContextIDInput: tmpState.ContextIDInput, Context: tmpState.Context } ];

		// Subject + tabs.
		if (tmpState.SubjectKind && tmpState.SubjectRecord)
		{
			this._deriveSubject(tmpState);
			tmpState.SubjectSlot = [ tmpState ];
			tmpState.EmptySlot = [];
		}
		else if (tmpState.SubjectKind && !tmpState.SubjectRecord)
		{
			// selected but still loading (or failed) — keep the empty pane minimal
			tmpState.SubjectSlot = [];
			tmpState.EmptySlot = [ {} ];
		}
		else
		{
			tmpState.SubjectSlot = [];
			tmpState.EmptySlot = [ {} ];
		}
	}

	_railGroup(pKey, pLabel, pIcon, pRecords, pKind, pServerFiltered)
	{
		const tmpActiveKind = this.state.SubjectKind;
		const tmpActiveID = this.state.SubjectID;
		const tmpRows = (pRecords || []).map((pRecord) =>
		{
			if (pKind === 'user')
			{
				return {
					ID: pRecord.IDUser,
					Label: libUserSearch.userLabel(pRecord),
					Sub: pRecord.LoginID || pRecord.Email || '',
				};
			}
			return {
				ID: pRecord.IDRole,
				Label: pRecord.Name || `Role ${pRecord.IDRole}`,
				Sub: (Number(pRecord.IDCustomer) === 0) ? 'Global' : (pRecord.PermissionLevel || ''),
			};
		})
			// Users are already server-filtered (pServerFiltered) — don't re-filter them client-side, or a hit on a
			// field not shown in the row label (e.g. email) would be dropped. Roles/Groups filter on the search box.
			.filter((pRow) => pServerFiltered || this._matchesSearch(pRow.Label) || this._matchesSearch(pRow.Sub))
			.map((pRow) => Object.assign(pRow, {
				Kind: pKind,
				RowClass: (tmpActiveKind === pKind && Number(tmpActiveID) === Number(pRow.ID)) ? 'is-active' : '',
			}));
		return { Key: pKey, Label: pLabel, Icon: pIcon, Rows: tmpRows, Count: tmpRows.length, HintSlot: [] };
	}

	_deriveSubject(pState)
	{
		const tmpIsUser = (pState.SubjectKind === 'user');
		// User Groups (Role.Type==='UserGroup') get an extra "Projects Granted" tab; other roles/groups don't.
		const tmpTabs = tmpIsUser ? TABS_USER : (this.isUserGroup ? TABS_ROLE.concat([ TAB_PROJECTS_GRANTED ]) : TABS_ROLE);

		pState.SubjectTitle = pState.SubjectLabel;
		pState.SubjectIcon = tmpIsUser ? 'fas fa-user' : (pState.SubjectKind === 'group' ? 'fas fa-layer-group' : 'fas fa-user-shield');
		pState.SubjectKindLabel = tmpIsUser ? 'User' : (this.isUserGroup ? 'User Group' : (pState.SubjectKind === 'group' ? 'Permission Group' : 'Role'));

		pState.Tabs = libTabBar.buildTabs(tmpTabs, pState.ActiveTab, (pTab) => `_Pict.providers.AccessWorkspace.navigateTab('${ pTab.Key }')`);

		// Per-tab body: phase 0 renders Details for real; not-yet-ready tabs show a placeholder. Later phases
		// move their tab key into READY_TABS + add a sibling active-slot as they mount real content.
		const tmpActive = pState.ActiveTab;
		const tmpRoleEditable = (!tmpIsUser && !!this.isRoleManager);
		pState.DetailsReadSlot = (tmpActive === 'details' && !tmpRoleEditable) ? [ { Fields: this._detailsFields(pState) } ] : [];
		pState.DetailsEditSlot = (tmpActive === 'details' && tmpRoleEditable) ? [ this._detailsForm(pState) ] : [];
		pState.MatrixActiveSlot = (!tmpIsUser && tmpActive === 'permissions') ? [ {} ] : [];
		pState.EffectiveActiveSlot = (tmpActive === 'effective') ? [ {} ] : [];
		pState.CompositionActiveSlot = (!tmpIsUser && tmpActive === 'composition') ? [ {} ] : [];
		pState.MembersActiveSlot = (!tmpIsUser && tmpActive === 'members') ? [ {} ] : [];
		pState.ProjectsGrantedActiveSlot = (!tmpIsUser && this.isUserGroup && tmpActive === 'projectsgranted') ? [ {} ] : [];
		pState.AssignmentActiveSlot = (tmpIsUser && tmpActive === 'assignment') ? [ {} ] : [];
		pState.OverridesActiveSlot = (tmpIsUser && tmpActive === 'overrides') ? [ {} ] : [];
		pState.PlaceholderActiveSlot = (!READY_TABS[tmpActive]) ? [ { Text: this._placeholderText(tmpActive) } ] : [];

		// Let the active tab's provider recompute its slots within this render pass (pure; no network).
		if (pState.MatrixActiveSlot.length && this.pict.providers.PermissionMatrix)
		{
			this.pict.providers.PermissionMatrix.deriveMatrix();
		}
		if (pState.EffectiveActiveSlot.length && this.pict.providers.PermissionVisualizer)
		{
			this.pict.providers.PermissionVisualizer.deriveVisualizer();
		}
		if (pState.CompositionActiveSlot.length && this.pict.providers.GroupComposition)
		{
			this.pict.providers.GroupComposition.deriveComposition();
		}
		if (pState.MembersActiveSlot.length && this.pict.providers.GroupComposition)
		{
			this.pict.providers.GroupComposition.deriveMembership();
		}
		if ((pState.AssignmentActiveSlot.length || pState.OverridesActiveSlot.length) && this.pict.providers.RoleAssignment)
		{
			this.pict.providers.RoleAssignment.deriveAssignment();
		}
	}

	_detailsFields(pState)
	{
		const tmpRecord = pState.SubjectRecord || {};
		if (pState.SubjectKind === 'user')
		{
			return [
				{ Label: 'Name', Value: libUserSearch.userLabel(tmpRecord) },
				{ Label: 'Login', Value: tmpRecord.LoginID || '' },
				{ Label: 'Email', Value: tmpRecord.Email || '' },
				{ Label: 'Base role level', Value: String(tmpRecord.UserRole || tmpRecord.IDRole || '') },
				{ Label: 'Customer', Value: String(tmpRecord.IDCustomer || '') },
			].filter((pField) => pField.Value !== '');
		}
		return [
			{ Label: 'Name', Value: tmpRecord.Name || '' },
			{ Label: 'Type', Value: tmpRecord.Type || 'Role' },
			{ Label: 'Permission level', Value: tmpRecord.PermissionLevel || '' },
			{ Label: 'Level ordinal', Value: (tmpRecord.LevelOrdinal !== undefined && tmpRecord.LevelOrdinal !== null) ? String(tmpRecord.LevelOrdinal) : '' },
			{ Label: 'Scope', Value: (Number(tmpRecord.IDCustomer) === 0) ? 'Global (all customers)' : `Customer ${tmpRecord.IDCustomer}` },
			{ Label: 'Description', Value: tmpRecord.Description || '' },
		].filter((pField) => pField.Value !== '');
	}

	/**
	 * Build the editable Details form model for a role/group (role-manager view).
	 * @param {Object} pState
	 * @return {Object}
	 */
	_detailsForm(pState)
	{
		const tmpRecord = pState.SubjectRecord || {};
		const tmpType = tmpRecord.Type || 'Role';
		const tmpIsNew = (Number(pState.SubjectID) <= 0);
		return {
			IDRole: pState.SubjectID,
			IsNew: tmpIsNew,
			Name: tmpRecord.Name || '',
			Description: tmpRecord.Description || '',
			PermissionLevel: tmpRecord.PermissionLevel || '',
			TypeOptions:
			[
				{ Value: 'Role', Label: 'Role', SelectedAttr: (tmpType !== 'PermissionGroup' && tmpType !== 'UserGroup') ? 'selected' : '' },
				{ Value: 'PermissionGroup', Label: 'Permission Group', SelectedAttr: (tmpType === 'PermissionGroup') ? 'selected' : '' },
				{ Value: 'UserGroup', Label: 'User Group', SelectedAttr: (tmpType === 'UserGroup') ? 'selected' : '' },
			],
			Scope: (Number(tmpRecord.IDCustomer) === 0) ? 'Global (all customers)' : `Customer ${tmpRecord.IDCustomer || this.idCustomer}`,
			SaveDisabledAttr: pState.Saving ? 'disabled' : '',
			DuplicateButtonSlot: (!tmpIsNew) ? [ { IDRole: pState.SubjectID } ] : [],
			DeleteButtonSlot: (!tmpIsNew && !pState.DeleteConfirm) ? [ {} ] : [],
			DeleteConfirmSlot: (!tmpIsNew && pState.DeleteConfirm) ? [ {} ] : [],
			// "Add members" panel on the Details (View/Create Role) screen. Existing role → Add button (assigns now);
			// new role → staged, assigned on Save. This form only renders for Role Managers, so no extra gate needed.
			AddMembersSlot:
			[
				{
					AddButtonSlot: (!tmpIsNew) ? [ {} ] : [],
					NewHintSlot: tmpIsNew ? [ {} ] : [],
					SavingSlot: pState.DetailsAddSaving ? [ {} ] : [],
					NoticeSlot: pState.DetailsAddNotice ? [ { Notice: pState.DetailsAddNotice } ] : [],
					ErrorSlot: pState.DetailsAddError ? [ { Error: pState.DetailsAddError } ] : [],
				},
			],
		};
	}

	// --- Add members from the Details (View / Create Role) screen ---

	/** (Re)mount the multi-select user picker into #acw-details-add-members-picker (Details edit form). Called from
	 * the view's onAfterRender; no-ops unless the mount is present (Details tab, Role Manager). */
	mountDetailsAddMembersPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#acw-details-add-members-picker').length === 0) { return; }
		const tmpView = tmpPicker.createPicker('AccessWorkspace-DetailsAddMembers',
			{
				DestinationAddress: '#acw-details-add-members-picker',
				ValueAddress: 'AppData.AccessWorkspace.DetailsAddUserIDs',
				Mode: 'multi',
				Searchable: true,
				Placeholder: 'Search users by name, login ID or email…',
				DataProvider: this._userPickerDataProvider(),
				ResolveValue: this._userPickerResolveValue(),
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	/** Server-side user search (customer-scoped) for the add-members picker. Empty term → browse the customer's users. */
	_userPickerDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._getJSON(pURL), { Scope: () => `FBV~IDCustomer~EQ~${this.idCustomer}~FBV~Deleted~EQ~0`, PageSize: 25 });
	}

	/** Resolve a selected user id → {Value,Text} so its chip shows a name. */
	_userPickerResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`User/${tmpID}`)
				.then((pUser) => ((pUser && pUser.IDUser) ? libUserSearch.userPickerOption(pUser) : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/** Assign users to a role at the customer scope (UserRoleJoin) in bounded-concurrency batches → { ok, fail }. */
	async _assignUsersToRoleAsync(pIDRole, pUserIDs)
	{
		const tmpIDs = (pUserIDs || []).map(Number).filter((pID) => pID > 0);
		let tmpOK = 0;
		let tmpFail = 0;
		const tmpConcurrency = 6;
		for (let tmpStart = 0; tmpStart < tmpIDs.length; tmpStart += tmpConcurrency)
		{
			const tmpBatch = tmpIDs.slice(tmpStart, tmpStart + tmpConcurrency);
			/* eslint-disable no-await-in-loop */
			await Promise.all(tmpBatch.map((pIDUser) =>
				this._putJSON('Permissions/Role/For/User', { IDRole: Number(pIDRole), IDUser: pIDUser, Context: 'Customer', IDContext: this.idCustomer })
					.then(() => { tmpOK++; }, () => { tmpFail++; })));
			/* eslint-enable no-await-in-loop */
		}
		return { ok: tmpOK, fail: tmpFail };
	}

	/** Existing role: assign the picked users immediately (the Details "Add users" button). A brand-new role has no
	 * id yet, so its picks are staged and assigned by saveDetailsAsync instead. */
	async addDetailsMembersAsync()
	{
		const tmpState = this.state;
		const tmpIDRole = Number(tmpState.SubjectID);
		if (!(tmpIDRole > 0) || tmpState.DetailsAddSaving) { return; }
		const tmpUserIDs = (tmpState.DetailsAddUserIDs || []).map(Number).filter((pID) => pID > 0);
		if (!tmpUserIDs.length)
		{
			tmpState.DetailsAddError = 'Search for and pick one or more users to add first.';
			tmpState.DetailsAddNotice = '';
			this._render();
			return;
		}
		tmpState.DetailsAddSaving = true;
		tmpState.DetailsAddError = '';
		tmpState.DetailsAddNotice = '';
		this._render();
		const tmpResult = await this._assignUsersToRoleAsync(tmpIDRole, tmpUserIDs);
		tmpState.DetailsAddSaving = false;
		tmpState.DetailsAddUserIDs = [];
		tmpState.DetailsAddNotice = tmpResult.fail
			? `Added ${tmpResult.ok} member${tmpResult.ok === 1 ? '' : 's'}; ${tmpResult.fail} could not be added (you may lack permission).`
			: `Added ${tmpResult.ok} member${tmpResult.ok === 1 ? '' : 's'} to this ${(tmpState.SubjectKind === 'group') ? 'group' : 'role'}.`;
		this._render();
		// Refresh the Members tab's data so it reflects the new members next time it's viewed.
		if (this.pict.providers.GroupComposition) { this.pict.providers.GroupComposition.loadMembersAsync(true); }
	}

	_placeholderText(pTab)
	{
		switch (pTab)
		{
		case 'permissions': return 'The permission matrix lands in the next phase.';
		case 'effective': return 'The effective-permissions visualizer lands in a later phase.';
		case 'composition': return 'Group composition (the role inheritance tree) lands in a later phase.';
		case 'members': return 'The members view lands in a later phase.';
		case 'assignment': return 'Role assignment lands in a later phase.';
		case 'overrides': return 'User permission overrides land in a later phase.';
		default: return 'Coming soon.';
		}
	}
}

module.exports = AccessWorkspaceProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'AccessWorkspace',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};
